import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ChatCircleText } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const MAX = 2000;

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const text = message.trim();
    if (!text) {
      toast.error("Please write a note before sending.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", { message: text });
      toast.success("Thanks — your circle admin will see this.");
      setMessage("");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't send feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating launcher — pinned to right edge, vertically centered */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed z-40 right-0 top-1/2 -translate-y-1/2 -mr-px translate-x-0 px-3 py-3 rounded-l-xl bg-forest text-gold border border-r-0 border-gold/40 hover:bg-forestSoft shadow-lg flex items-center gap-2 group transition-all"
        title="Send feedback"
        data-testid="feedback-launcher"
      >
        <ChatCircleText size={20} weight="duotone" />
        <span className="hidden lg:inline writing-mode-vertical text-[11px] uppercase tracking-widest">
          Feedback
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-forest">Send feedback</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-1">
            <p className="text-sm text-sageGray">
              Spot a bug, miss a feature, or have a thought to share? Your circle admin reads every note.
            </p>
            <div>
              <Textarea
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={MAX}
                placeholder="What's on your mind?"
                className="bg-white border-sage focus-visible:ring-gold"
                data-testid="feedback-textarea"
              />
              <div className="flex justify-end mt-1">
                <span className="text-[11px] text-sageGray" data-testid="feedback-counter">
                  {message.length}/{MAX}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
                className="text-sageGray hover:bg-cream rounded-full"
                data-testid="feedback-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-forest hover:bg-forestSoft text-ivory rounded-full px-6"
                data-testid="feedback-submit"
              >
                {submitting ? "Sending…" : "Send feedback"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
